import { PopperPlacementType } from "@mui/base/Popper";
import { genericMemo } from "@redotech/react-util/component";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { useSearch } from "@redotech/react-util/search";
import { DEFAULT_LOCALE, Translate, useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import {
  ArrayStringFilterOperator,
  textInputOperators,
  valuelessOperators,
} from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
  type RedoFilterGroupSize,
} from "../../arbiter-components/filter-group/redo-filter-group";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { RedoListItem } from "../../arbiter-components/list/redo-list";
import { RedoListDropdown } from "../../arbiter-components/list/redo-list-dropdown";
import { RedoMultiselectDropdown } from "../../arbiter-components/list/redo-multiselect-dropdown";
import { SimpleFilterDropdown } from "../../filters/simple-filter-dropdown";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  AdvancedTableFilter,
  ArrayStringTableFilter,
} from "../advanced-filter";
import { ArrayStringTextValueEditor } from "./array-string-text-value-editor";
import { ARRAY_STRING_FILTER_SEARCH_KEYS } from "./array-to-string-filter-search";
import {
  applyShortcutToggle,
  buildShortcutStaticItems,
} from "./array-to-string-filter-shortcuts";
import { cascadeSubmenuOffsetY } from "./cascade-submenu-alignment";
import * as styles from "./filter.module.css";

function useValueOptions({
  filter,
  searchString,
  filters,
}: {
  filter: ArrayStringTableFilter;
  searchString: string;
  filters: AdvancedTableFilter[];
}): LoadState<string[] | undefined> {
  const [fetchedValueOptions, fetchValueOptions] = useTriggerLoad(
    async (signal) => {
      if (filter.valueFetcher) {
        const body: {
          filters: AdvancedTableFilter[];
          filterSearch?: string;
          applyTableState?: boolean;
        } = {
          filters: filters.filter(
            (otherFilter) => filter.data.name !== otherFilter.data.name,
          ),
          applyTableState: filter.applyTableState,
        };

        if (filter.dbSearch) {
          body.filterSearch = searchString;
        }

        return await filter.valueFetcher(body, signal);
      } else {
        return filter.values ?? [];
      }
    },
  );

  const stableDefault = useMemo(() => {
    return { pending: false, error: false, value: filter.values ?? [] };
  }, [filter.values]);

  useEffect(() => {
    fetchValueOptions();
  }, [fetchValueOptions]);

  useEffect(() => {
    if (filter.dbSearch) {
      fetchValueOptions();
    }
  }, [filter.dbSearch, fetchValueOptions, searchString]);

  if (!filter.valueFetcher) {
    return stableDefault;
  }

  return fetchedValueOptions;
}

/**
 * The value-selection dropdown for an array-to-string filter, rendered without
 * the surrounding pill chip. Used both by `ArrayStringFilterGroup` (anchored to
 * the chip's value button) and by the add-filter menu (anchored to the menu
 * button) so a value can be chosen before a pill is committed.
 */
export const ArrayStringValueDropdown = genericMemo(
  function ArrayStringValueDropdown({
    filter,
    setFilter,
    filters,
    anchor,
    alignWithAnchorRow,
    open,
    setOpen,
    placement,
  }: {
    filter: ArrayStringTableFilter;
    setFilter(filter: ArrayStringTableFilter): void;
    filters: AdvancedTableFilter[];
    anchor: HTMLElement | null;
    /** Sit the first line of text on the anchor row's, for cascade submenus. */
    alignWithAnchorRow?: boolean;
    open: boolean;
    setOpen(open: boolean): void;
    placement?: PopperPlacementType;
  }) {
    const { t, i18n } = useTranslation();
    const { value, operator } = filter.data;
    const [searchText, setSearchText] = useState<string>("");

    const searcher = useMemo(
      () =>
        new Fuse<RedoListItem<string>>([], {
          keys: ARRAY_STRING_FILTER_SEARCH_KEYS,
          threshold: 0.3,
        }),
      [],
    );
    const debouncedFilterSearch = useDebounce(searchText, 300);
    const valueOptions = useValueOptions({
      filter,
      searchString: debouncedFilterSearch,
      filters,
    });

    const placeholder = searchPlaceholder(t, i18n.language, filter);
    const createItemText =
      filter.createItemText?.(t) ??
      t("Create {{item}}", { item: filter.itemLabel });

    const selectedItems = useMemo(() => {
      if (!value) {
        return [];
      }
      return value.map((item) => getStringValue(item, filter));
    }, [value, filter]);

    const multiSelectOptions: RedoListItem<string>[] = useMemo(() => {
      if (!valueOptions.value) {
        return [];
      }
      if (filter.itemGenerator) {
        return valueOptions.value.map(filter.itemGenerator);
      }
      return valueOptions.value.map<RedoListItem<string>>((item) => ({
        id: getStringValue(item, filter),
        value: getDisplayString(item, filter),
        type: "text",
        text: getDisplayString(item, filter),
      }));
    }, [valueOptions, filter]);

    const selectedListItems: RedoListItem<string>[] = useMemo(() => {
      if (filter.itemGenerator) {
        return selectedItems.map(filter.itemGenerator);
      }
      return selectedItems.map<RedoListItem<string>>((item) => ({
        id: getStringValue(item, filter),
        value: getDisplayString(item, filter),
        type: "text",
        text: getDisplayString(item, filter),
      }));
    }, [selectedItems, filter]);

    // When dbSearch is true the backend already filters by the search string;
    // passing it again to Fuse would double-filter and drop valid matches.
    const clientSideSearchText = filter.dbSearch ? "" : debouncedFilterSearch;

    const searchedItems = useSearch(
      searcher,
      multiSelectOptions,
      clientSideSearchText,
    );

    const shortcutStaticItems = useMemo(
      () => buildShortcutStaticItems(filter.shortcuts),
      [filter.shortcuts],
    );

    const handleShortcutSelected = useMemo(
      () => makeShortcutItemSelectedHandler(filter, setFilter),
      [filter, setFilter],
    );

    // Only reachable from a pill, never the cascade: a field whose value is
    // typed is click-to-add, so no submenu ever opens on this editor.
    if (textInputOperators.includes(operator)) {
      return (
        <ArrayStringTextValueEditor
          anchor={anchor}
          multiValue={filter.multiValueInput}
          open={open}
          placeholder={textValuePlaceholder(t, filter)}
          placement={placement}
          setOpen={setOpen}
          setValues={(values) =>
            setFilter({ ...filter, data: { ...filter.data, value: values } })
          }
          values={selectedItems}
        />
      );
    }

    const offsetY = alignWithAnchorRow
      ? cascadeSubmenuOffsetY("searchHeader")
      : undefined;

    return filter.useSingleSelect ? (
      <RedoListDropdown
        dropdownAnchor={anchor}
        dropdownOffsetY={offsetY}
        dropdownOpen={open}
        dropdownPlacement={placement}
        items={searchedItems}
        itemSelected={({ item }) => {
          setFilter({
            ...filter,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            data: { ...filter.data, value: [item.id as string] },
          });
        }}
        itemsLoading={valueOptions.pending}
        searchPlaceholder={placeholder}
        searchString={searchText}
        selectedItems={selectedItems[0]}
        setDropdownOpen={setOpen}
        setSearchString={setSearchText}
        size="xs"
        sortBySelected
      />
    ) : (
      <RedoMultiselectDropdown
        dropdownAnchor={anchor}
        dropdownOffsetY={offsetY}
        dropdownOpen={open}
        dropdownPlacement={placement}
        footer={
          filter.onRequestCreateItem ? (
            <RedoButton
              hierarchy="tertiary"
              IconLeading={PlusIcon}
              onClick={filter.onRequestCreateItem}
              size="xs"
              text={createItemText}
            />
          ) : undefined
        }
        items={searchedItems}
        itemsLoading={valueOptions.pending}
        searchPlaceholder={placeholder}
        searchString={searchText}
        selectedItems={selectedListItems}
        setDropdownOpen={setOpen}
        setSearchString={setSearchText}
        setSelectedItems={(items) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const selectedItems = items.map((item) => item.id) as string[];
          setFilter({
            ...filter,
            data: { ...filter.data, value: selectedItems },
          });
        }}
        sortBySelected
        staticItems={
          shortcutStaticItems.length > 0 ? shortcutStaticItems : undefined
        }
        staticItemSelected={
          shortcutStaticItems.length > 0 ? handleShortcutSelected : undefined
        }
      />
    );
  },
);

export const ArrayStringFilterGroup = genericMemo(
  function ArrayStringFilterGroup({
    filter,
    setFilter,
    removeFilter,
    openOnRender,
    filters,
    size,
    selectedPropertyFields,
    onPropertyFieldsChange,
  }: {
    filter: ArrayStringTableFilter;
    setFilter(filter: ArrayStringTableFilter): void;
    removeFilter(): void;
    openOnRender: boolean;
    filters: AdvancedTableFilter[];
    size?: RedoFilterGroupSize;
    selectedPropertyFields?: string[];
    onPropertyFieldsChange?: (fields: string[]) => void;
  }) {
    const { t, i18n } = useTranslation();
    const [valueRef, setValueRef] = useState<HTMLButtonElement | null>(null);
    const [propertyNameRef, setPropertyNameRef] =
      useState<HTMLButtonElement | null>(null);
    const [propertyNameDropdownOpen, setPropertyNameDropdownOpen] =
      useState(false);

    const { value, operator } = filter.data;

    const [searchText, setSearchText] = useState<string>("");

    const searcher = useMemo(
      () =>
        new Fuse<RedoListItem<string>>([], {
          keys: ARRAY_STRING_FILTER_SEARCH_KEYS,
          threshold: 0.3,
        }),
      [],
    );

    const debouncedFilterSearch = useDebounce(searchText, 300);

    const valueOptions = useValueOptions({
      filter,
      searchString: debouncedFilterSearch,
      filters,
    });

    useEffect(() => {
      if (openOnRender && valueRef) {
        valueRef.click();
      }
    }, [openOnRender, valueRef]);

    const emptyValueText = filter.emptyValueText ?? "...";

    const valueText = filter.useSingleSelect
      ? value && value.length > 0
        ? getDisplayString(value[0], filter)
        : emptyValueText
      : value && value.length > 0
        ? filter.getValueSummary
          ? filter.getValueSummary(value)
          : selectedCountSummary(t, i18n.language, filter, value.length)
        : emptyValueText;

    const valueInput = (
      <RedoTextInput
        className={styles.stringInput}
        fullWidth={false}
        placeholder={
          value?.[0] ? getDisplayString(value[0], filter) : emptyValueText
        }
        setValue={(value) => {
          setFilter({
            ...filter,
            data: { ...filter.data, value: [getStringValue(value, filter)] },
          });
        }}
        state="default"
        value={value?.[0] != null ? getStringValue(value[0], filter) : ""}
      />
    );

    const valueDropdownAnchor = (
      <RedoFilterDropdownAnchor
        color="primary"
        onClick={() => {
          setValueDropdownOpen(!valueDropdownOpen);
        }}
        ref={setValueRef}
        text={valueText}
        tooltip={
          value &&
          value.length > 0 && (
            <Flex dir="column">
              {value.map((item) => (
                <Text key={item}>{getDisplayString(item, filter)}</Text>
              ))}
            </Flex>
          )
        }
        weight="medium"
      />
    );

    const selectedItems = useMemo(() => {
      if (!value) {
        return [];
      }
      return value.map((item) => getStringValue(item, filter));
    }, [value, filter]);

    const multiSelectOptions: RedoListItem<string>[] = useMemo(() => {
      if (!valueOptions.value) {
        return [];
      }

      if (filter.itemGenerator) {
        return valueOptions.value.map(filter.itemGenerator);
      }

      return valueOptions.value.map<RedoListItem<string>>((item) => ({
        id: getStringValue(item, filter),
        // Under normal circumstances, we would want this to be getStringValue,
        // but because of the custom render function we need a consistent reference to search by
        value: getDisplayString(item, filter),
        type: "text",
        text: getDisplayString(item, filter),
      }));
    }, [valueOptions, filter]);

    const selectedListItems: RedoListItem<string>[] = useMemo(() => {
      if (filter.itemGenerator) {
        return selectedItems.map(filter.itemGenerator);
      }

      return selectedItems.map<RedoListItem<string>>((item) => ({
        id: getStringValue(item, filter),
        // Under normal circumstances, we would want this to be getStringValue,
        // but because of the custom render function we need a consistent reference to search by
        value: getDisplayString(item, filter),
        type: "text",
        text: getDisplayString(item, filter),
      }));
    }, [selectedItems, filter]);

    const placeholder = searchPlaceholder(t, i18n.language, filter);
    const createItemText =
      filter.createItemText?.(t) ??
      t("Create {{item}}", { item: filter.itemLabel });

    const optionsAlreadyFilteredByBackend = filter.dbSearch;
    const clientSideOptionSearchText = optionsAlreadyFilteredByBackend
      ? ""
      : debouncedFilterSearch;

    const searchedItems = useSearch(
      searcher,
      multiSelectOptions,
      clientSideOptionSearchText,
    );

    const [valueDropdownOpen, setValueDropdownOpen] = useState(false);

    const shortcutStaticItems = useMemo(
      () => buildShortcutStaticItems(filter.shortcuts),
      [filter.shortcuts],
    );

    const handleShortcutSelected = useMemo(
      () => makeShortcutItemSelectedHandler(filter, setFilter),
      [filter, setFilter],
    );

    const valueDropdown = filter.useSingleSelect ? (
      <RedoListDropdown
        dropdownAnchor={valueRef}
        dropdownOpen={valueDropdownOpen}
        items={searchedItems}
        itemSelected={({ item }) => {
          setFilter({
            ...filter,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            data: { ...filter.data, value: [item.id as string] },
          });
        }}
        itemsLoading={valueOptions.pending}
        searchPlaceholder={placeholder}
        searchString={searchText}
        selectedItems={selectedItems[0]}
        setDropdownOpen={setValueDropdownOpen}
        setSearchString={setSearchText}
        size="xs"
        sortBySelected
      />
    ) : (
      <RedoMultiselectDropdown
        dropdownAnchor={valueRef}
        dropdownOpen={valueDropdownOpen}
        footer={
          filter.onRequestCreateItem ? (
            <RedoButton
              hierarchy="tertiary"
              IconLeading={PlusIcon}
              onClick={filter.onRequestCreateItem}
              size="xs"
              text={createItemText}
            />
          ) : undefined
        }
        items={searchedItems}
        itemsLoading={valueOptions.pending}
        searchPlaceholder={placeholder}
        searchString={searchText}
        selectedItems={selectedListItems}
        setDropdownOpen={setValueDropdownOpen}
        setSearchString={setSearchText}
        setSelectedItems={(items) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const selectedItems = items.map((item) => item.id) as string[];
          setFilter({
            ...filter,
            data: { ...filter.data, value: selectedItems },
          });
        }}
        sortBySelected
        staticItems={
          shortcutStaticItems.length > 0 ? shortcutStaticItems : undefined
        }
        staticItemSelected={
          shortcutStaticItems.length > 0 ? handleShortcutSelected : undefined
        }
      />
    );

    const propertyFieldMultiselect = filter.propertyNameMultiselect;
    const selectedPropertyFieldItems = useMemo(() => {
      if (!propertyFieldMultiselect) {
        return [];
      }
      const selected = new Set(selectedPropertyFields ?? []);
      return propertyFieldMultiselect.options.filter((item) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        selected.has(item.id as string),
      );
    }, [propertyFieldMultiselect, selectedPropertyFields]);

    const propertyNameSlot = propertyFieldMultiselect ? (
      <>
        <RedoFilterDropdownAnchor
          color="primary"
          hoverless
          IconTrailing={ChevronDownIcon}
          onClick={() => setPropertyNameDropdownOpen((open) => !open)}
          ref={setPropertyNameRef}
          text={filter.displayName}
          weight="medium"
        />
        <RedoMultiselectDropdown
          dropdownAnchor={propertyNameRef}
          dropdownOpen={propertyNameDropdownOpen}
          items={propertyFieldMultiselect.options}
          searchPlaceholder={propertyFieldMultiselect.placeholder}
          selectedItems={selectedPropertyFieldItems}
          setDropdownOpen={setPropertyNameDropdownOpen}
          setSelectedItems={(items) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            onPropertyFieldsChange?.(items.map((item) => item.id as string))
          }
        />
      </>
    ) : undefined;

    const usesTextInput = textInputOperators.includes(operator);
    const usesChipEditor = usesTextInput && !!filter.multiValueInput;

    return (
      <>
        {usesChipEditor ? (
          <ArrayStringTextValueEditor
            anchor={valueRef}
            multiValue
            open={valueDropdownOpen}
            placeholder={textValuePlaceholder(t, filter)}
            setOpen={setValueDropdownOpen}
            setValues={(values) =>
              setFilter({ ...filter, data: { ...filter.data, value: values } })
            }
            values={selectedItems}
          />
        ) : usesTextInput ? undefined : (
          valueDropdown
        )}
        <RedoFilterGroup
          Icon={filter.Icon}
          propertyName={filter.displayName}
          propertyNameSlot={propertyNameSlot}
          query={
            <SimpleFilterDropdown
              filterStyle="query"
              options={filter.operators || defaultOperators}
              optionToFriendlyName={(value) =>
                getArrayStringOperatorLabel(t, value)
              }
              setValue={(value) => {
                let shouldResetValue = false;
                if (hasOperatorTypeChanged(operator, value)) {
                  // we just switched operator input types, so we should reset the value
                  shouldResetValue = true;
                }

                setFilter({
                  ...filter,
                  data: {
                    ...filter.data,
                    operator: value,
                    value: shouldResetValue ? [] : filter.data.value,
                  },
                });
              }}
              value={operator}
            />
          }
          removeFilter={removeFilter}
          size={size}
          value={
            valuelessOperators.includes(operator)
              ? null
              : usesTextInput && !usesChipEditor
                ? valueInput
                : valueDropdownAnchor
          }
        />
      </>
    );
  },
);

function getArrayStringOperatorLabel(
  t: Translate,
  operator: ArrayStringFilterOperator,
): string {
  switch (operator) {
    case ArrayStringFilterOperator.CONTAINS:
      return t("contains", { context: "filter operator" });
    case ArrayStringFilterOperator.NOT_CONTAINS:
      return t("does not contain", { context: "filter operator" });
    case ArrayStringFilterOperator.MATCHES_EXACTLY:
      return t("matches exactly", { context: "filter operator" });
    case ArrayStringFilterOperator.ANY_OF:
      return t("include any of", { context: "filter operator" });
    case ArrayStringFilterOperator.NONE_OF:
      return t("include none of", { context: "filter operator" });
    case ArrayStringFilterOperator.ALL_OF:
      return t("have all of", { context: "filter operator" });
    case ArrayStringFilterOperator.IS_EMPTY:
      return t("is empty", { context: "filter operator" });
    case ArrayStringFilterOperator.IS_NOT_EMPTY:
      return t("is not empty", { context: "filter operator" });
    case ArrayStringFilterOperator.STARTS_WITH:
      return t("starts with", { context: "filter operator" });
    case ArrayStringFilterOperator.DOES_NOT_START_WITH:
      return t("does not start with", { context: "filter operator" });
  }
}

/**
 * Appending "s" only pluralizes English, so outside English a filter without
 * `itemLabelPlural` falls back to copy that does not name the item.
 */
function pluralItemLabel(
  language: string,
  filter: ArrayStringTableFilter,
): string | undefined {
  if (filter.itemLabelPlural) {
    return filter.itemLabelPlural;
  }
  return language === DEFAULT_LOCALE ? `${filter.itemLabel}s` : undefined;
}

function searchPlaceholder(
  t: Translate,
  language: string,
  filter: ArrayStringTableFilter,
): string {
  const items = pluralItemLabel(language, filter);
  return items ? t("Search {{items}}...", { items }) : t("Search...");
}

function selectedCountSummary(
  t: Translate,
  language: string,
  filter: ArrayStringTableFilter,
  count: number,
): string {
  const items =
    count === 1 ? filter.itemLabel : pluralItemLabel(language, filter);
  return items
    ? t("{{count}} {{items}}", { count, items })
    : t("{{count}} selected", { count });
}

function textValuePlaceholder(
  t: Translate,
  filter: ArrayStringTableFilter,
): string {
  return t("Enter {{item}}...", { item: filter.itemLabel });
}

function getStringValue(item: any, filter: ArrayStringTableFilter): string {
  return filter.stringify && item != null
    ? filter.stringify(item)
    : item != null
      ? item.toString()
      : "";
}

function getDisplayString(item: any, filter: ArrayStringTableFilter): string {
  return filter.display && item
    ? filter.display(item)
    : item
      ? item.toString()
      : "";
}

export const defaultOperators = [
  ArrayStringFilterOperator.ANY_OF,
  ArrayStringFilterOperator.NONE_OF,
  ArrayStringFilterOperator.ALL_OF,
  ArrayStringFilterOperator.CONTAINS,
  ArrayStringFilterOperator.NOT_CONTAINS,
];

function hasOperatorTypeChanged(
  before: ArrayStringFilterOperator,
  after: ArrayStringFilterOperator,
): boolean {
  const arrays = [textInputOperators, valuelessOperators];

  const beforeIncluded = arrays.some((arr) => arr.includes(before));
  const afterIncluded = arrays.some((arr) => arr.includes(after));

  return beforeIncluded !== afterIncluded;
}

function makeShortcutItemSelectedHandler(
  filter: ArrayStringTableFilter,
  setFilter: (filter: ArrayStringTableFilter) => void,
) {
  return (item: RedoListItem<void>) => {
    const { shortcuts } = filter;
    if (!shortcuts) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const shortcutId = (item.id as string).replace(/^shortcut-/, "");
    const shortcut = shortcuts.find((s) => s.id === shortcutId);
    if (!shortcut) {
      return;
    }
    const newValues = applyShortcutToggle(filter.data.value ?? [], shortcut);
    setFilter({ ...filter, data: { ...filter.data, value: newValues } });
  };
}
